import { useEffect, useState } from 'react'
import { Field, Icon, Stack, Text } from '@/design-system'
import { cn } from '@/lib/cn'
import select from '@/features/onboarding/SelectCard.module.css'
import styles from './Activate.module.css'

/** How a charge is paid. Charges happen on activation, never at registration. */
export type PayMethod = 'apple' | 'google' | 'card' | 'bank' | 'balance'

export interface MethodOption {
  readonly id: PayMethod
  readonly label: string
  readonly detail: string
  readonly icon: string
  /** Fee as a fraction of the amount. */
  readonly rate: number
  readonly disabled?: boolean
}

export const METHODS: Record<PayMethod, MethodOption> = {
  apple: { id: 'apple', label: 'Apple Pay', detail: 'Instant', icon: 'phone_iphone', rate: 0.005 },
  google: { id: 'google', label: 'Google Pay', detail: 'Instant', icon: 'android', rate: 0.005 },
  card: { id: 'card', label: 'Debit card', detail: 'Instant', icon: 'credit_card', rate: 0.005 },
  bank: {
    id: 'bank',
    label: 'Bank transfer',
    detail: 'Free · usually same day',
    icon: 'account_balance',
    rate: 0,
  },
  balance: {
    id: 'balance',
    label: 'US Dollar account',
    detail: 'Instant · no fee',
    icon: 'account_balance_wallet',
    rate: 0,
  },
}

export function MethodPicker({
  options,
  value,
  onChange,
}: {
  readonly options: readonly MethodOption[]
  readonly value: PayMethod
  readonly onChange: (method: PayMethod) => void
}) {
  return (
    <div className={styles.methods} role="radiogroup" aria-label="How to pay">
      {options.map((option) => {
        const on = option.id === value
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={option.disabled}
            className={cn(select.option, on && select.selected)}
            onClick={() => onChange(option.id)}
            style={option.disabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
          >
            <Icon name={option.icon} size={20} />
            <Stack gap={0} align="start">
              <Text variant="caption" weight="semibold">
                {option.label}
              </Text>
              <Text variant="caption" tone="secondary">
                {option.detail}
              </Text>
            </Stack>
          </button>
        )
      })}
    </div>
  )
}

/** Luhn check — catches the typo before the card network does. */
export function luhn(number: string): boolean {
  const digits = number.replace(/\D/g, '')
  if (digits.length < 13 || digits.length > 19) return false
  let sum = 0
  for (let i = 0; i < digits.length; i += 1) {
    let d = Number(digits[digits.length - 1 - i])
    if (i % 2 === 1) {
      d *= 2
      if (d > 9) d -= 9
    }
    sum += d
  }
  return sum % 10 === 0
}

function expiryOk(value: string): boolean {
  const match = /^(\d{2})\s*\/\s*(\d{2})$/.exec(value)
  if (!match) return false
  const month = Number(match[1])
  const year = 2000 + Number(match[2])
  if (month < 1 || month > 12) return false
  const now = new Date()
  return year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1)
}

/** Card entry for the debit-card method. Reports whether it can be charged. */
export function CardFields({ onValid }: { readonly onValid: (valid: boolean) => void }) {
  const [number, setNumber] = useState('')
  const [expiry, setExpiry] = useState('')
  const [cvc, setCvc] = useState('')
  const [touched, setTouched] = useState(false)

  const valid = { number: luhn(number), expiry: expiryOk(expiry), cvc: /^\d{3,4}$/.test(cvc) }

  // Unmounting drops what was typed, so the parent must not keep it as valid.
  useEffect(() => () => onValid(false), [onValid])
  const all = valid.number && valid.expiry && valid.cvc

  const report = (next: { number?: string; expiry?: string; cvc?: string }) => {
    const n = next.number ?? number
    const e = next.expiry ?? expiry
    const c = next.cvc ?? cvc
    onValid(luhn(n) && expiryOk(e) && /^\d{3,4}$/.test(c))
  }

  return (
    <Stack gap={3} onBlur={() => setTouched(true)}>
      <Field
        label="Card number"
        inputMode="numeric"
        autoComplete="cc-number"
        value={number}
        onChange={(event) => {
          const next = event.target.value
            .replace(/\D/g, '')
            .slice(0, 19)
            .replace(/(\d{4})(?=\d)/g, '$1 ')
          setNumber(next)
          report({ number: next })
        }}
        {...(touched && number && !valid.number
          ? { error: 'That card number does not check out.' }
          : {})}
      />
      {/* Each field in its own flexible wrapper: side by side at their
          natural width, the two pushed a phone screen 90px wide. */}
      <Stack direction="row" gap={3}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field
            size={7}
            label="Expiry"
            placeholder="MM / YY"
            inputMode="numeric"
            autoComplete="cc-exp"
            value={expiry}
            onChange={(event) => {
              const raw = event.target.value.replace(/\D/g, '').slice(0, 4)
              const next = raw.length > 2 ? `${raw.slice(0, 2)} / ${raw.slice(2)}` : raw
              setExpiry(next)
              report({ expiry: next })
            }}
            {...(touched && expiry && !valid.expiry ? { error: 'Expired or not a date.' } : {})}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field
            size={4}
            label="CVC"
            inputMode="numeric"
            autoComplete="cc-csc"
            value={cvc}
            onChange={(event) => {
              const next = event.target.value.replace(/\D/g, '').slice(0, 4)
              setCvc(next)
              report({ cvc: next })
            }}
          />
        </div>
      </Stack>
      {all && (
        <Stack direction="row" align="center" gap={2}>
          <Icon name="verified" size={16} />
          <Text variant="caption" tone="secondary">
            Card details look right.
          </Text>
        </Stack>
      )}
    </Stack>
  )
}
